import { useMemo, useState } from "react";
import api from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Coffee, Flag, Plus, Download } from "lucide-react";
import Avatar from "@/components/Avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import WeeklyBacklogSection from "@/components/WeeklyBacklogSection";
import { STATUS_META, STATUS_ORDER as _SO, PRIORITY_META, priorityOf, priorityRank } from "@/constants/status";

// Inline status pip options — kept in sync with STATUS_COLOR keys below.
const STATUS_OPTIONS = _SO.map((k) => ({ key: k, label: STATUS_META[k].label, dot: STATUS_META[k].dot }));

const STATUS_COLOR = Object.fromEntries(_SO.map((k) => [k, STATUS_META[k].pill]));

/** Get a task's personal status for a given assignee row.
 * Single-assignee tasks: always use the top-level rollup (per_assignee_status
 * can be stale for them since managers historically wrote status directly).
 * Multi-assignee: use the row-owner's per-slot status. */
function statusFor(task, ownerId) {
  const isMulti = (task.assignees || []).length > 1;
  if (!isMulti) return task.status;
  if (!ownerId) return task.status;
  const slot = (task.assignee_details || []).find((a) => a.user_id === ownerId);
  if (slot && slot.status) return slot.status;
  return task.status;
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Start-of-week Monday for any given date. Returns a new Date at 00:00 local time. */
function startOfWeek(d) {
  const base = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = base.getDay(); // 0=Sun ... 6=Sat
  const offset = dow === 0 ? -6 : 1 - dow; // shift back to Monday
  base.setDate(base.getDate() + offset);
  return base;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Weekly grid: rows = employees, columns = Mon-Sun. Managers can drag task chips
 * across cells to reschedule (preserves time-of-day; defaults to 18:00 if the task
 * had no deadline). Clicking a chip opens the task dialog. Clicking + on a cell
 * (managers only) creates a new task pre-assigned to that user on that date.
 */
export default function WeeklyGridView({
  users,          // array of user rows to render (already filtered upstream)
  tasks,          // filtered task list (respect the global filter bar)
  currentUserId,
  isManager,
  onEdit,
  onAddOnDateForUser, // (date: Date, userId: string) => void
  onDropTask,     // (taskId: string, date: Date, ownerId: string) => Promise<void> | void
  onStatusChange, // (task, newStatus, meta) => void — status pip on chip
  leavesByUser = {}, // { userId: ["YYYY-MM-DD", ...] } — filed leaves per person
  backlog = null,    // { team, users, currentUser, onTaskCreated } — managers/admins only
}) {
  const today = new Date();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(today));
  const [dragOverKey, setDragOverKey] = useState(null);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
  }, [weekStart]);

  // Map<userId, Map<dayKey, task[]>>
  const grid = useMemo(() => {
    const g = new Map();
    users.forEach((u) => g.set(u.id, new Map()));
    // For every visible day of this week, gather tasks whose scheduled window
    // (start_date → deadline) overlaps that day. This means a Mon–Wed task now
    // renders a chip on Mon AND Tue AND Wed, matching the way managers plan
    // multi-day shoots/edits.
    const dayIso = (d) => isoDate(d);
    tasks.forEach((t) => {
      const startRaw = (t.start_date || "").slice(0, 10) || null;
      const endRaw = t.deadline ? new Date(t.deadline) : null;
      const endIso = endRaw && !isNaN(endRaw.getTime()) ? isoDate(endRaw) : null;
      if (!startRaw && !endIso) return; // Up Next tasks don't show on the grid
      const spanStart = startRaw || endIso;
      const spanEnd = endIso || startRaw;
      const assigneeIds = (t.assignees && t.assignees.length ? t.assignees : [t.assigned_to]).filter(Boolean);
      weekDays.forEach((wd) => {
        const dayKey = dayIso(wd);
        if (dayKey < spanStart || dayKey > spanEnd) return;
        const seen = new Set();
        assigneeIds.forEach((uid) => {
          if (seen.has(uid) || !g.has(uid)) return;
          seen.add(uid);
          const row = g.get(uid);
          if (!row.has(dayKey)) row.set(dayKey, []);
          row.get(dayKey).push(t);
        });
      });
    });
    // Sort each cell: priority first, then earlier time first
    g.forEach((row) => {
      row.forEach((arr) => {
        arr.sort((a, b) => {
          if (priorityRank(a) !== priorityRank(b)) return priorityRank(a) - priorityRank(b);
          const ad = a.deadline ? new Date(a.deadline).getTime() : Infinity;
          const bd = b.deadline ? new Date(b.deadline).getTime() : Infinity;
          return ad - bd;
        });
      });
    });
    return g;
  }, [users, tasks, weekDays]);

  const shiftWeek = (deltaDays) => {
    setWeekStart((cur) => {
      const d = new Date(cur);
      d.setDate(cur.getDate() + deltaDays);
      return d;
    });
  };
  const goToday = () => setWeekStart(startOfWeek(new Date()));

  const rangeLabel = (() => {
    const start = weekDays[0];
    const end = weekDays[6];
    const sameMonth = start.getMonth() === end.getMonth();
    const fmt = (d, withYear) => d.toLocaleString(undefined, { month: "short", day: "numeric", ...(withYear ? { year: "numeric" } : {}) });
    return `${sameMonth ? start.toLocaleString(undefined, { day: "numeric" }) : fmt(start)} – ${fmt(end, true)}`;
  })();

  return (
    <>
    {backlog && <WeeklyBacklogSection weekStart={weekStart} {...backlog} />}
    <div className="rounded-xl border border-white/8 bg-surface overflow-hidden" data-testid="weekly-grid-view">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-surface">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => shiftWeek(-7)} data-testid="weekly-prev-btn"
            className="text-white/60 hover:text-white hover:bg-white/5">
            <ChevronLeft size={16} />
          </Button>
          <div className="font-display text-lg tracking-tight min-w-[180px] text-center" data-testid="weekly-range-label">
            {rangeLabel}
          </div>
          <Button variant="ghost" size="sm" onClick={() => shiftWeek(7)} data-testid="weekly-next-btn"
            className="text-white/60 hover:text-white hover:bg-white/5">
            <ChevronRight size={16} />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={goToday} data-testid="weekly-today-btn"
            className="text-white/60 hover:text-white hover:bg-white/5">
            This week
          </Button>
          <Button
            variant="outline" size="sm"
            data-testid="weekly-plan-pdf-btn"
            onClick={async () => {
              const iso = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, "0")}-${String(weekStart.getDate()).padStart(2, "0")}`;
              try {
                const res = await api.get(`/reports/weekly-plan-pdf?week_start=${iso}`, { responseType: "blob" });
                const blob = new Blob([res.data], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `weekly-plan-${iso}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (err) {
                toast.error(err.response?.data?.detail || "Failed to download weekly plan PDF");
              }
            }}
            className="border-amber-500/40 text-amber-200 hover:border-amber-400 hover:text-white hover:bg-amber-500/10"
          >
            <Download size={12} className="mr-1.5" /> Plan PDF
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Header row: employee col + 7 day cols */}
          <div className="grid grid-cols-[180px_repeat(7,minmax(0,1fr))] border-b border-white/5 bg-surface">
            <div className="px-3 py-2 overline border-r border-white/5">Employee</div>
            {weekDays.map((d, i) => {
              const isToday = sameDay(d, today);
              return (
                <div
                  key={i}
                  className={`px-3 py-2 overline border-r border-white/5 last:border-r-0 ${isToday ? "text-[#E11D2E]" : ""}`}
                  data-testid={`weekly-col-header-${isoDate(d)}`}
                >
                  <div>{DAYS[i]}</div>
                  <div className={`text-[0.7rem] font-mono ${isToday ? "text-[#E11D2E]" : "text-white/30"}`}>
                    {d.getDate()}/{d.getMonth() + 1}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Employee rows */}
          {users.length === 0 ? (
            <div className="p-6 text-sm text-white/40 italic">No members match the current filters.</div>
          ) : (
            users.map((u) => (
              <div
                key={u.id}
                className="grid grid-cols-[180px_repeat(7,minmax(0,1fr))] border-b border-white/5 last:border-b-0"
              >
                <div className="px-3 py-2 border-r border-white/5 bg-surface flex items-center gap-2 sticky left-0 z-10">
                  <Avatar user={u} size={28} />
                  <div className="min-w-0">
                    <div className="text-xs font-medium truncate">
                      {u.name}
                    </div>
                    <div className="text-[0.7rem] text-white/40 font-mono truncate">{u.team}</div>
                  </div>
                </div>
                {weekDays.map((d) => {
                  const key = `${u.id}::${isoDate(d)}`;
                  const cellTasks = grid.get(u.id)?.get(isoDate(d)) || [];
                  const isToday = sameDay(d, today);
                  const dropActive = dragOverKey === key;
                  const isLeaveDay = (leavesByUser[u.id] || []).includes(isoDate(d));
                  return (
                    <div
                      key={key}
                      data-testid={`weekly-cell-${u.id}-${isoDate(d)}`}
                      onDragOver={(e) => {
                        if (!isManager) return;
                        e.preventDefault();
                        setDragOverKey(key);
                      }}
                      onDragLeave={() => setDragOverKey((k) => (k === key ? null : k))}
                      onDrop={(e) => {
                        setDragOverKey(null);
                        if (!isManager) return;
                        const taskId = e.dataTransfer.getData("text/task-id");
                        if (taskId) onDropTask?.(taskId, d, u.id);
                      }}
                      className={`min-h-[92px] border-r border-white/5 last:border-r-0 p-1.5 transition-colors ${
                        isToday ? "bg-white/[0.02]" : ""
                      } ${isLeaveDay ? "bg-amber-500/[0.06]" : ""} ${dropActive ? "bg-[#E11D2E]/10 ring-1 ring-inset ring-[#E11D2E]/40" : ""}`}
                    >
                      {isLeaveDay && (
                        <div
                          data-testid={`weekly-leave-badge-${u.id}-${isoDate(d)}`}
                          className="text-[0.65rem] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1 py-0.5 mb-1 inline-flex items-center gap-1"
                        ><Coffee size={9} /> On leave</div>
                      )}
                      <div className="flex items-center justify-end mb-1">
                        {isManager && (
                          <button
                            type="button"
                            onClick={() => onAddOnDateForUser?.(d, u.id)}
                            data-testid={`weekly-add-${u.id}-${isoDate(d)}`}
                            title="Add task"
                            className="text-white/25 hover:text-[#E11D2E] transition"
                          >
                            <Plus size={11} />
                          </button>
                        )}
                      </div>
                      <div className="space-y-1">
                        {cellTasks.map((t) => {
                          const movable = isManager;
                          const canChangeStatus = !!onStatusChange && (isManager || u.id === currentUserId);
                          const rowStatus = statusFor(t, u.id);
                          return (
                            <div
                              key={t.id}
                              className={`w-full flex items-stretch gap-0.5 rounded overflow-hidden ${STATUS_COLOR[rowStatus] || STATUS_COLOR.not_started}`}
                            >
                              {canChangeStatus && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      data-testid={`weekly-status-pip-${u.id}-${t.id}`}
                                      title="Change status"
                                      onClick={(e) => e.stopPropagation()}
                                      className="shrink-0 w-3.5 flex items-center justify-center bg-black/25 hover:bg-black/40 transition"
                                    >
                                      <span className={`h-2 w-2 rounded-full ${STATUS_OPTIONS.find((s) => s.key === rowStatus)?.dot || "bg-white/40"}`} />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="bg-surface border-white/10 text-white min-w-[150px]" align="start">
                                    {STATUS_OPTIONS.map((opt) => (
                                      <DropdownMenuItem
                                        key={opt.key}
                                        data-testid={`weekly-status-option-${u.id}-${t.id}-${opt.key}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (opt.key === rowStatus) return;
                                          if (opt.key === "on_hold") {
                                            // Hold reason is OPTIONAL now — flip status without asking.
                                            onStatusChange(t, "on_hold", { hold_reason: t.hold_reason || "" });
                                          } else if (opt.key === "incomplete") {
                                            const reason = window.prompt("Reason it stayed incomplete?");
                                            if (!reason?.trim()) return;
                                            onStatusChange(t, "incomplete", { incomplete_reason: reason.trim() });
                                          } else {
                                            onStatusChange(t, opt.key, {});
                                          }
                                        }}
                                        className="text-xs cursor-pointer flex items-center gap-2"
                                      >
                                        <span className={`h-2 w-2 rounded-full ${opt.dot}`} />
                                        {opt.label}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                              <button
                                type="button"
                                draggable={movable}
                                onDragStart={(e) => {
                                  if (!movable) return;
                                  e.dataTransfer.effectAllowed = "move";
                                  e.dataTransfer.setData("text/task-id", t.id);
                                }}
                                onClick={() => onEdit?.(t)}
                                title={`${[t.client, t.project_name].filter(Boolean).join(" · ")}${t.client || t.project_name ? " — " : ""}${t.title}${movable ? " (drag to reschedule)" : ""}`}
                                data-testid={`weekly-task-${u.id}-${t.id}`}
                                className={`flex-1 min-w-0 text-left text-[0.7rem] px-1.5 py-1 hover:brightness-125 transition ${movable ? "cursor-grab active:cursor-grabbing" : ""}`}
                              >
                                {(t.client || t.project_name) && (
                                  <div className="text-[0.6rem] uppercase tracking-wider opacity-70 truncate leading-none mb-0.5">
                                    <span className="client-line">{t.client}</span>{t.project_name ? ` · ${t.project_name}` : ""}
                                  </div>
                                )}
                                <div className="flex items-center gap-1 truncate">
                                  {priorityOf(t) !== "normal" && <Flag size={9} fill="currentColor" className={`shrink-0 ${PRIORITY_META[priorityOf(t)].text}`} />}
                                  <span className="truncate">{t.title}</span>
                                </div>
                              </button>
                            </div>
                          );
                        })}
                        {cellTasks.length === 0 && (
                          <div className="text-[0.7rem] text-white/20 italic pl-0.5">
                            {dropActive ? "Drop here" : ""}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>

      {isManager && (
        <div className="px-4 py-2 text-[0.7rem] text-white/40 border-t border-white/5 bg-surface">
          Tip: drag any task chip across cells to reschedule to a different day or reassign to another team member.
        </div>
      )}
    </div>
    </>
  );
}
